import * as React from 'react'
import { withNamespaces } from 'react-i18next'
import { connect } from 'react-redux'
import {
    Row,
    Col,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    Table,
    Input
} from 'reactstrap'
import Select from 'react-select'
import Spinner from 'react-bootstrap/Spinner'
import { AvForm, AvField } from 'availity-reactstrap-validation'
import { toast } from 'react-toastify'
import { useQuery } from '@tanstack/react-query'
import IndustriesService from '../../../../api/IndustriesService'

const CreateAndEditModal = ({
    t,
    lng,
    show,
    toggle,
    onSubmit,
    analystMemoList,
    countriesMemoList,
    parentEntitiesMemoList,
    submitInProgress = false,
    entity = null,
    Modules: {
        active: currentModule
    }
}) => {
    const avFormRef = React.useRef()
    const [addressIndexes, setAddressIndexes] = React.useState([0])
    const [selectedAnalyst, setSelectedAnalyst] = React.useState(null)
    const [parentEntity, setSelectedParentEntity] = React.useState(null)
    const [isUltimateLegal, setIsUltimateLegal] = React.useState(false)
    const [selectedIndusries, setSelectedIndustries] = React.useState([])
    const [industriesQuery, setIndustriesQuery] = React.useState({
        isLoading: false,   
        itemsCount: 0,
        pageIndex: 1,
        pageSize: 1000,
        searchText: '',
        items: []
    })

    const industriesService = new IndustriesService()

    const {
        refetch: refetchIndustries
    } = useQuery({
        queryKey: [
            'entity-management-search-industries-query',
            lng
        ],
        queryFn: async () => {
            setIndustriesQuery((currentState) => {
                return {
                    ...currentState,
                    isLoading: true
                }
            })

            return await industriesService.fetchList({
                language: lng !== 'en' ? lng : undefined,
                pageSize: industriesQuery.pageSize,
                pageIndex: industriesQuery.pageIndex,
                name: industriesQuery.searchText || undefined
            })
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: false,
        onError: () => {
            toast(t('An error occurred while fetching industries list.'), {
                type: 'error',
            })
        },
        onSuccess: (response) => {
            setIndustriesQuery((currentState) => {
                return {
                    ...currentState,
                    isLoading: false,
                    // itemsCount: parseInt(response.itemsCount),
                    // pageIndex: parseInt(response.pageIndex),
                    // pageSize: parseInt(response.pageSize),
                    items: (response || []).map((industry) => {
                        return {
                            value: industry.id,
                            label: `${industry.class} - ${industry.title}`
                        }
                    })
                }
            })
        },
        onSettled: () => {
            setIndustriesQuery((currentState) => {
                return {
                    ...currentState,
                    isLoading: false
                }
            })
        }
    })

    React.useEffect(() => {
        if(show && currentModule === 'tp'){
            refetchIndustries()
        }

        if (entity) {
            setSelectedParentEntity({
                value: entity?.parent?.id,
                label: entity?.parent?.title
            })

            setSelectedAnalyst(entity?.successor_analyst ? {
                value: entity.successor_analyst.id,
                label: entity.successor_analyst.full_name
            } : null)

            setIsUltimateLegal(!entity.parent?.id)

            if(entity.nace_code?.id){
                setSelectedIndustries([{
                    value: entity.nace_code?.id,
                    label: `${entity.nace_code.product_group} - ${entity.nace_code.activity}`,
                }])
            }
            else{
                setSelectedIndustries([])
            }
        } else {
            setSelectedAnalyst(null)
            setIsUltimateLegal(false)
            setSelectedIndustries([])
        }
    }, [show])

    React.useEffect(() => {
        if(show && currentModule === 'tp'){
            const timeOutId = setTimeout(() => {
                refetchIndustries()
            }, 500)

            return () => clearTimeout(timeOutId)
        }
    }, [
        industriesQuery.searchText,
        show
    ])

    React.useEffect(() => {
        if(selectedIndusries.length && industriesQuery.items.length){
            const result = selectedIndusries.map((sIndustry) => {
                const translatedItem = industriesQuery.items.find((listItem) => listItem.value === sIndustry.value)

                return translatedItem || sIndustry
            })

            setSelectedIndustries(result)
        }
    }, [
        t,
        industriesQuery.items
    ])

    return (
        <Modal size='xl' scrollable={true} isOpen={show} backdrop='static'>
            <AvForm
                className='needs-validation'
                onValidSubmit={(e, values) => {
                    onSubmit &&
                        onSubmit(
                            {
                                ...values,
                                naceCode: selectedIndusries[0]?.value || undefined,
                                legalEntityLocations: entity
                                    ? undefined
                                    : (
                                          values.legalEntityLocations || []
                                      ).filter((item) => !!item)
                            },
                            entity ? 'edit' : 'create'
                        )
                }}
                ref={avFormRef}
            >
                <ModalHeader toggle={toggle}>
                    {entity ? (
                        <React.Fragment>{`${t('Entity')} ${
                            entity?.id
                        }`}</React.Fragment>
                    ) : (
                        <React.Fragment>{t('Add A New Entity')}</React.Fragment>
                    )}
                </ModalHeader>

                <ModalBody 
                    style={{
                        maxHeight: 'calc(100vh - 168px)'
                    }}>
                    <Row>
                        <Col sm='12' md='6' className='mb-3'>
                            <Label
                                className='form-label'
                                htmlFor='entity-company-full-name'
                            >
                                {t('Company Full Name')}:
                            </Label>

                            <AvField
                                name='title'
                                type='text'
                                errorMessage={t('This field cannot be blank')}
                                className='form-control'
                                validate={{
                                    required: {
                                        value: true
                                    }
                                }}
                                defaultValue={entity?.title}
                                id='entity-company-full-name'
                            />
                        </Col>
                        <Col sm='12' md='6' className='mb-3'>
                            <Label
                                className='form-label'
                                htmlFor='entity-company-short-name'
                            >
                                {t('Short Name')}:
                            </Label>

                            <AvField
                                name='shortName'
                                type='text'
                                errorMessage={t('This field cannot be blank')}
                                className='form-control'
                                validate={{
                                    required: {
                                        value: false
                                    }
                                }}
                                defaultValue={entity?.short_name}
                                id='entity-company-short-name'
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col sm='12' md='6' className='mb-3'>
                            <Label
                                className='form-label'
                                htmlFor='entity-company-dc-number'
                            >
                                {t('Debtor/Creditor number')}:
                            </Label>

                            <AvField
                                name='dcNumber'
                                type='text'
                                errorMessage={t('This field cannot be blank')}
                                className='form-control'
                                validate={{
                                    required: {
                                        value: false
                                    }
                                }}
                                defaultValue={entity?.dc_number}
                                id='entity-company-dc-number'
                            />
                        </Col>

                        <Col sm='12' md='6' className='mb-3'>
                            <Label
                                className='form-label'
                                htmlFor='entity-company-mandateid'
                            >
                                {t('Mandate ID')}:
                            </Label>

                            <AvField
                                name='mandantId'
                                type='number'
                                errorMessage={t('This field cannot be blank')}
                                className='form-control'
                                validate={{
                                    required: {
                                        value: false
                                    }
                                }}
                                defaultValue={entity?.mandant_id}
                                id='entity-company-mandateid'
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col sm='6' className='mb-3'>
                            <Label
                                className='form-label'
                                htmlFor='entity-successor-analyst'
                            >
                                {t('Assign By Default')}:
                            </Label>
                            <Select
                                id='entity-successor-analyst'
                                className='mb-2'
                                placeholder={t('Select') + '...'}
                                classNamePrefix='select2-selection'
                                options={analystMemoList}
                                menuPortalTarget={document.body}
                                defaultValue={selectedAnalyst}
                                isClearable
                                onChange={(e) => {
                                    const input =
                                        avFormRef.current._inputs
                                            .successorAnalyst
                                    if (input) {
                                        input.value = e?.value
                                        // input.validate()
                                    }
                                }}
                                styles={{
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999
                                    })
                                }}
                            />
                            <AvField
                                type='hidden'
                                name='successorAnalyst'
                                errorMessage={t('This field cannot be blank')}
                                defaultValue={selectedAnalyst?.value}
                                validate={{
                                    required: {
                                        value: false
                                    }
                                }} 
                            />
                        </Col>

                        {currentModule === 'tp' && (
                            <Col sm="6" className="mb-3">
                                <Label for="selected-industries">
                                    {t("Industry")}: 
                                </Label>

                                <Select
                                    id='selected-industries'
                                    placeholder={t("Select") + "..."}
                                    classNamePrefix="select2-selection"
                                    menuPortalTarget={document.body}
                                    isLoading={ industriesQuery.isLoading }
                                    options={industriesQuery.items}
                                    onChange={(e) => setSelectedIndustries([e])}
                                    value={selectedIndusries[0]}
                                    isMulti={false}
                                    filterOption={(e) => e}
                                    onInputChange={(input) => {
                                        setIndustriesQuery((currentState) => {
                                            return {
                                                ...currentState,
                                                searchText: input
                                            }
                                        })
                                    }}
                                    styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999
                                        })
                                    }}
                                />
                            </Col>
                        )}

                        <AvField
                            name="naceCode"
                            type="hidden"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false }
                            }}
                            value={selectedIndusries}
                        />
                    </Row>

                    <Row>
                        <Col sm="12" className="mb-3 d-flex align-items-center">
                            <Label className="form-label me-2 mb-0" htmlFor="isUltimateLegal">
                                {t("Ultimate Parent")}:
                            </Label>
                            <Input
                                type="checkbox"
                                id="isUltimateLegal"
                                checked={isUltimateLegal}
                                onChange={() => setIsUltimateLegal(!isUltimateLegal)}
                            />
                        </Col>
                    </Row>

                    {!isUltimateLegal && (
                        <Row>
                            <Col sm="12" md="6" className="mb-3">
                                <Label className="form-label" htmlFor="entity-parent">
                                    {t("Parent Entity")}:
                                </Label>
                                <Select
                                    id="entity-parent"
                                    className="mb-2"
                                    placeholder={t("Select") + "..."}
                                    classNamePrefix="select2-selection"
                                    options={parentEntitiesMemoList}
                                    menuPortalTarget={document.body}
                                    defaultValue={parentEntity}
                                    onChange={(e) => {
                                        const input = avFormRef.current._inputs.parent
                                        if (input) {
                                            input.value = e?.value
                                            input.validate()
                                        }
                                    }}
                                    styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999
                                        })
                                    }}
                                    isClearable
                                />
                                <AvField
                                    type="hidden"
                                    name="parent"
                                    errorMessage={t("This field cannot be blank")}
                                    defaultValue={parentEntity?.value}
                                    validate={{
                                        required: { value: false }
                                    }}
                                />
                            </Col>
                        </Row>
                    )}

                    {!entity && (
                        <Row>
                            <Col sm='12' className='mb-3'>
                                <Label className='form-label'>
                                    {t('Address')}:
                                </Label>
                                <Table borderless>
                                    <tbody>
                                        {addressIndexes.map((addressIndex) => {
                                            return (
                                                <tr key={addressIndex}>
                                                    <td style={{width: '200px'}}>
                                                        <Select
                                                            placeholder={
                                                                t('Country') +
                                                                '...'
                                                            }
                                                            classNamePrefix='select2-selection'
                                                            options={
                                                                countriesMemoList
                                                            }
                                                            menuPortalTarget={
                                                                document.body
                                                            }
                                                            width='100%'
                                                            onChange={(e) => {
                                                                const input =
                                                                    avFormRef
                                                                        .current
                                                                        ._inputs[
                                                                        `legalEntityLocations[${addressIndex}][country]`
                                                                    ]
                                                                if (input) {
                                                                    input.value =
                                                                        e.value
                                                                    input.validate()
                                                                }
                                                            }}
                                                            styles={{
                                                                menuPortal: (
                                                                    base
                                                                ) => ({
                                                                    ...base,
                                                                    zIndex: 9999
                                                                }),
                                                                control: (
                                                                    base
                                                                ) => ({
                                                                    ...base,
                                                                    minWidth:
                                                                        '160px'
                                                                })
                                                            }}
                                                        />
                                                        <AvField
                                                            type='hidden'
                                                            name={`legalEntityLocations[${addressIndex}][country]`}
                                                            errorMessage={t(
                                                                'This field cannot be blank'
                                                            )}
                                                            validate={{
                                                                required: {
                                                                    value: true
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{width: '200px'}}>
                                                        <AvField
                                                            name={`legalEntityLocations[${addressIndex}][postalCode]`}
                                                            type='text'
                                                            errorMessage={t(
                                                                'This field cannot be blank'
                                                            )}
                                                            placeholder={t(
                                                                'Postal Code'
                                                            )}
                                                            className='form-control'
                                                            validate={{
                                                                required: {
                                                                    value: false
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{width: '200px'}}>
                                                        <AvField
                                                            name={`legalEntityLocations[${addressIndex}][city]`}
                                                            type='text'
                                                            errorMessage={t(
                                                                'This field cannot be blank'
                                                            )}
                                                            placeholder={t(
                                                                'City'
                                                            )}
                                                            className='form-control'
                                                            validate={{
                                                                required: {
                                                                    value: true
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <AvField
                                                            name={`legalEntityLocations[${addressIndex}][address]`}
                                                            type='text'
                                                            placeholder={`${t('Street, Number')}...`}
                                                            errorMessage={t(
                                                                'This field cannot be blank'
                                                            )}
                                                            className='form-control'
                                                            validate={{
                                                                required: {
                                                                    value: true
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Button
                                                            hidden={
                                                                addressIndexes.length <
                                                                2
                                                            }
                                                            size='sm'
                                                            color='danger'
                                                            outline
                                                            className='border-0'
                                                            type='button'
                                                            onClick={() => {
                                                                setAddressIndexes(
                                                                    (
                                                                        currentList
                                                                    ) => {
                                                                        return [
                                                                            ...currentList.filter(
                                                                                (
                                                                                    item
                                                                                ) => {
                                                                                    return (
                                                                                        item !==
                                                                                        addressIndex
                                                                                    )
                                                                                }
                                                                            )
                                                                        ]
                                                                    }
                                                                )
                                                            }}
                                                        >
                                                            <i className='ri-delete-bin-line font-size-24' />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </Table>
                                <div className='d-flex justify-content-end'>
                                    <Button
                                        color='primary'
                                        outline
                                        type='button'
                                        onClick={() => {
                                            setAddressIndexes((currentList) => {
                                                return [
                                                    ...currentList,
                                                    [...currentList]
                                                        .sort()
                                                        .reverse()[0] + 1
                                                ]
                                            })
                                        }}
                                    >
                                        <i className='ri-add-line me-2' />
                                        {t('Add Location')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button
                        color='primary'
                        outline
                        className='waves-effect waves-light'
                        type='button'
                        onClick={toggle}
                        disabled={submitInProgress}
                    >
                        {t('Cancel')}
                    </Button>

                    <Button
                        color='primary'
                        type='submit'
                        className='waves-effect waves-light'
                        disabled={submitInProgress}
                    >
                        {submitInProgress && (
                            <Spinner
                                animation='border'
                                variant='white'
                                size='sm'
                                className='me-1'
                            />
                        )}
                        {entity ? (
                            <React.Fragment>
                                {t('Save Changes')}
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                {t('Add Entity')}
                            </React.Fragment>
                        )}
                    </Button>
                </ModalFooter>
            </AvForm>
        </Modal>
    )
}

const mapToState = (state) => {
    const { Modules } = state

    return {
        Modules
    }
}

export default withNamespaces()(
    connect(mapToState, null)(
        CreateAndEditModal
    )
)
